import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CheckCircle2, Loader2, CalendarPlus, CalendarDays, X, ChevronDown, ChevronUp, CreditCard, ChevronRight, RotateCcw, Check, Calendar as CalendarIcon, Banknote, ExternalLink, Lock } from 'lucide-react';
import { services, pricing } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { StripePaymentSheet } from './StripePaymentSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHARED_API = process.env.REACT_APP_SHARED_API_URL;

const NO_PACKAGE_SERVICES = ['Vinyl Wrap', 'Ambient Lighting', 'Starlight Headliner', 'Remove Tint'];
const NO_TINT_SHADE_SERVICES = ['Chrome Delete'];
const WINDSHIELD_ADDON_PACKAGES = ['Front Two Windows', 'Sedan & Coupe', 'Trucks & SUV'];
const NO_DEPOSIT_PACKAGES = ['Front Windshield Only', 'Front Two Windows'];

const STARLIGHT_PACKAGES = [
  { label: '450 Stars — $800', value: '450 Stars - $800' },
  { label: '600 Stars — $950', value: '600 Stars - $950' },
  { label: '800 Stars — $1,000', value: '800 Stars - $1000' },
  { label: '1,000 Stars — $1,200', value: '1000 Stars - $1200' },
];

const getDepositAmount = (service) => {
  if (service === 'Remove Tint') return 100;
  if (service === 'Chrome Delete') return 100;
  return NO_PACKAGE_SERVICES.includes(service) ? 200 : 50;
};

// Returns the full service price (or null if unknown / contact-for-quote)
const getServiceTotalPrice = (service, pkg, starlightPkg) => {
  if (!service) return null;
  if (service === 'Starlight Headliner') {
    const s = starlightPkg || pkg || '';
    const m = s.match(/\$(\d+)/);
    return m ? parseInt(m[1]) : null;
  }
  if (service === 'Ambient Lighting') return 500;
  if (service === 'Chrome Delete') {
    if (!pkg) return null;
    const p = pkg.toLowerCase();
    if (p.includes('window trim')) return 200;
    if (p.includes('door handle')) return 150;
    if (p.includes('emblem') || p.includes('badge')) return 100;
    return null; // Special Request
  }
  if (service === 'Remove Tint' || service === 'Tint Removal') {
    if (!pkg) return null;
    const p = pkg.toLowerCase();
    if (p.includes('fix-it') || (p.includes('sides') && p.includes('rear'))) return 150;
    if (p.includes('full vehicle')) return 250;
    if (p.includes('windshield')) return 100;
    if (p.includes('front two')) return 100;
    return null;
  }
  if (service === 'Window Tinting' || service === 'Window Tint') {
    if (!pkg) return null;
    const isCeramic = pkg.includes('Ceramic');
    const p = pkg.replace('(Premium Carbon)', '').replace('(Ceramic)', '').trim().toLowerCase();
    if (p.includes('sedan') && p.includes('full')) return isCeramic ? 400 : 300;
    if (p.includes('sedan')) return isCeramic ? 300 : 200;
    if ((p.includes('truck') || p.includes('suv')) && p.includes('full')) return isCeramic ? 500 : 400;
    if (p.includes('truck') || p.includes('suv')) return isCeramic ? 400 : 300;
    if (p.includes('windshield')) return isCeramic ? 150 : 100;
    if (p.includes('front two')) return isCeramic ? 150 : 100;
    return null;
  }
  return null; // Vinyl Wrap and others: contact for quote
};

const needsDeposit = () => true;

const needsWindshieldAddon = (pkg) => {
  if (pkg.toLowerCase().includes('full')) return false; // full packages already include windshield
  return WINDSHIELD_ADDON_PACKAGES.some(p => pkg.startsWith(p));
};

// ── Choose Shade helpers ──────────────────────────────────────────────────────
const isFrontTwoWindowsPkg = (pkg) => pkg.includes('Front Two Windows');
const isFrontWindshieldOnlyPkg = (pkg) => pkg.includes('Front Windshield Only');

const showFrontsRow = (pkg) => !isFrontWindshieldOnlyPkg(pkg);
const showRearsRow = (pkg) => !isFrontTwoWindowsPkg(pkg) && !isFrontWindshieldOnlyPkg(pkg);
// Wind row shows for: full packages, windshield-only, OR any package where customer added windshield
const showWindRow = (pkg, addWindshield) =>
  pkg.toLowerCase().includes('full') ||
  isFrontWindshieldOnlyPkg(pkg) ||
  addWindshield === 'yes';
// Show "Add Windshield" field for packages that don't already include windshield
const showWindshieldField = (pkg) =>
  !!pkg && !pkg.toLowerCase().includes('full') && !isFrontWindshieldOnlyPkg(pkg);

const VLT_OPTIONS = [5, 15, 20, 35, 50];

const vltToImageCode = (vlt) => {
  if (vlt === 5) return '05';
  if (vlt === 15 || vlt === 20) return '15';
  if (vlt === 35) return '35';
  if (vlt === 50) return '70';
  return null;
};

const buildTintShadeString = (fronts, rears, wind, pkg, addWindshield) => {
  const parts = [];
  if (showFrontsRow(pkg) && fronts) parts.push(`Fronts ${fronts}%`);
  if (showRearsRow(pkg) && rears) parts.push(`Rears ${rears}%`);
  if (showWindRow(pkg, addWindshield) && wind) parts.push(`Wind ${wind}%`);
  return parts.join(', ');
};

const parseSavedShade = (shadeStr, field) => {
  if (!shadeStr) return 0;
  const m = shadeStr.match(new RegExp(`${field} (\\d+)%`));
  return m ? parseInt(m[1]) : 0;
};

const BLOCKED_DATE_RANGES = [];

const BLOCKED_TIME_SLOTS = {
};

const isDateBlocked = (dateStr) => {
  if (!dateStr) return false;
  return BLOCKED_DATE_RANGES.some(range => dateStr >= range.start && dateStr <= range.end);
};

const isTimeBlocked = (dateStr, time) => {
  if (!dateStr || !BLOCKED_TIME_SLOTS[dateStr]) return false;
  return BLOCKED_TIME_SLOTS[dateStr].includes(time);
};

const currentYear = new Date().getFullYear();
const vehicleYears = Array.from({ length: 30 }, (_, i) => String(currentYear + 1 - i));

const vehicleMakes = {
  'Acura': ['CL', 'ILX', 'Integra', 'Legend', 'MDX', 'NSX', 'RDX', 'RL', 'RLX', 'RSX', 'TL', 'TLX', 'TSX', 'ZDX'],
  'Alfa Romeo': ['4C', 'Giulia', 'Stelvio', 'Tonale'],
  'Aston Martin': ['DB11', 'DB12', 'DBX', 'Vantage'],
  'Audi': ['A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'e-tron', 'e-tron GT', 'Q3', 'Q4 e-tron', 'Q5', 'Q7', 'Q8', 'R8', 'RS3', 'RS5', 'RS6', 'RS7', 'S3', 'S4', 'S5', 'S6', 'S7', 'SQ5', 'SQ7', 'SQ8', 'TT'],
  'Bentley': ['Bentayga', 'Continental GT', 'Flying Spur'],
  'BMW': ['1 Series', '2 Series', '3 Series', '4 Series', '5 Series', '6 Series', '7 Series', '8 Series', 'i4', 'i5', 'i7', 'iX', 'M2', 'M3', 'M4', 'M5', 'M8', 'X1', 'X2', 'X3', 'X4', 'X5', 'X6', 'X7', 'Z4'],
  'Buick': ['Cascada', 'Enclave', 'Encore', 'Encore GX', 'Envision', 'Envista', 'LaCrosse', 'LeSabre', 'Lucerne', 'Park Avenue', 'Regal', 'Rendezvous', 'Riviera', 'Verano'],
  'Cadillac': ['ATS', 'CT4', 'CT5', 'CT6', 'CTS', 'DeVille', 'DTS', 'Eldorado', 'Escalade', 'Escalade ESV', 'Lyriq', 'SRX', 'STS', 'XT4', 'XT5', 'XT6', 'XTS'],
  'Chevrolet': ['Astro', 'Avalanche', 'Aveo', 'Blazer', 'Bolt EUV', 'Bolt EV', 'Camaro', 'Caprice', 'Cavalier', 'Cobalt', 'Colorado', 'Corvette', 'Cruze', 'El Camino', 'Equinox', 'Express', 'HHR', 'Impala', 'Malibu', 'Monte Carlo', 'Nova', 'S-10', 'Silverado 1500', 'Silverado 2500', 'Silverado 3500', 'Sonic', 'Spark', 'SS', 'SSR', 'Suburban', 'Tahoe', 'TrailBlazer', 'Traverse', 'Trax', 'Volt'],
  'Chrysler': ['200', '300', 'Aspen', 'Concorde', 'Crossfire', 'LHS', 'Pacifica', 'PT Cruiser', 'Sebring', 'Town & Country', 'Voyager'],
  'Dodge': ['Avenger', 'Caliber', 'Caravan', 'Challenger', 'Charger', 'Dakota', 'Dart', 'Durango', 'Grand Caravan', 'Hornet', 'Journey', 'Magnum', 'Neon', 'Nitro', 'Ram 1500', 'Ram 2500', 'Ram 3500', 'Stratus', 'Viper'],
  'Ferrari': ['296', '488', '812', 'F8', 'Portofino', 'Purosangue', 'Roma', 'SF90'],
  'Fiat': ['124 Spider', '500', '500L', '500X'],
  'Ford': ['Bronco', 'Bronco Sport', 'C-Max', 'Crown Victoria', 'E-Series', 'EcoSport', 'Edge', 'Escape', 'Excursion', 'Expedition', 'Explorer', 'F-150', 'F-150 Lightning', 'F-250', 'F-350', 'Fiesta', 'Five Hundred', 'Flex', 'Focus', 'Freestar', 'Freestyle', 'Fusion', 'Galaxy', 'GT', 'Maverick', 'Mustang', 'Mustang Mach-E', 'Ranger', 'Taurus', 'Thunderbird', 'Transit', 'Windstar'],
  'Genesis': ['G70', 'G80', 'G90', 'GV60', 'GV70', 'GV80'],
  'GMC': ['Acadia', 'Canyon', 'Envoy', 'Hummer EV', 'Jimmy', 'Safari', 'Savana', 'Sierra 1500', 'Sierra 2500', 'Sierra 3500', 'Sonoma', 'Terrain', 'Yukon', 'Yukon XL'],
  'Honda': ['Accord', 'Civic', 'Clarity', 'CR-V', 'CR-Z', 'Crosstour', 'Element', 'Fit', 'HR-V', 'Insight', 'Odyssey', 'Passport', 'Pilot', 'Prelude', 'Prologue', 'Ridgeline', 'S2000'],
  'Hyundai': ['Accent', 'Azera', 'Elantra', 'Entourage', 'Equus', 'Genesis', 'Genesis Coupe', 'Ioniq', 'Ioniq 5', 'Ioniq 6', 'Kona', 'Kona Electric', 'Nexo', 'Palisade', 'Santa Cruz', 'Santa Fe', 'Sonata', 'Tiburon', 'Tucson', 'Veloster', 'Venue', 'Veracruz', 'XG350'],
  'Infiniti': ['EX', 'FX', 'G25', 'G35', 'G37', 'I30', 'I35', 'J30', 'JX', 'M35', 'M37', 'M45', 'M56', 'Q40', 'Q45', 'Q50', 'Q60', 'Q70', 'QX4', 'QX30', 'QX50', 'QX55', 'QX56', 'QX60', 'QX70', 'QX80'],
  'Jaguar': ['E-Pace', 'F-Pace', 'F-Type', 'I-Pace', 'XE', 'XF', 'XJ', 'XK'],
  'Jeep': ['Cherokee', 'Commander', 'Compass', 'Gladiator', 'Grand Cherokee', 'Grand Cherokee L', 'Grand Wagoneer', 'Liberty', 'Patriot', 'Renegade', 'Wagoneer', 'Wrangler'],
  'Kia': ['Carnival', 'Cadenza', 'EV6', 'EV9', 'Forte', 'K5', 'K900', 'Niro', 'Optima', 'Rio', 'Rondo', 'Sedona', 'Seltos', 'Sorento', 'Soul', 'Spectra', 'Sportage', 'Stinger', 'Telluride'],
  'Lamborghini': ['Aventador', 'Huracan', 'Revuelto', 'Urus'],
  'Land Rover': ['Defender', 'Discovery', 'Discovery Sport', 'Range Rover', 'Range Rover Evoque', 'Range Rover Sport', 'Range Rover Velar'],
  'Lexus': ['CT', 'ES', 'GS', 'GX', 'IS', 'LC', 'LS', 'LX', 'NX', 'RC', 'RX', 'RZ', 'SC', 'TX', 'UX'],
  'Lincoln': ['Aviator', 'Continental', 'Corsair', 'MKC', 'MKS', 'MKT', 'MKX', 'MKZ', 'Nautilus', 'Navigator', 'Town Car', 'Zephyr'],
  'Lucid': ['Air', 'Gravity'],
  'Maserati': ['Ghibli', 'GranTurismo', 'Grecale', 'Levante', 'MC20', 'Quattroporte'],
  'Mazda': ['3', '5', '6', 'CX-3', 'CX-30', 'CX-5', 'CX-50', 'CX-70', 'CX-9', 'CX-90', 'MX-5 Miata', 'MX-30', 'RX-7', 'RX-8', 'Tribute'],
  'McLaren': ['570S', '600LT', '720S', '750S', 'Artura', 'GT'],
  'Mercedes-Benz': ['A-Class', 'AMG GT', 'B-Class', 'C-Class', 'CLA', 'CLE', 'CLS', 'E-Class', 'EQB', 'EQE', 'EQS', 'G-Class', 'GLA', 'GLB', 'GLC', 'GLE', 'GLS', 'Maybach', 'S-Class', 'SL', 'SLC', 'Sprinter'],
  'Mini': ['Clubman', 'Convertible', 'Countryman', 'Hardtop'],
  'Mitsubishi': ['Eclipse', 'Eclipse Cross', 'Galant', 'Lancer', 'Mirage', 'Outlander', 'Outlander Sport', 'Pajero'],
  'Nissan': ['350Z', '370Z', 'Altima', 'Armada', 'Frontier', 'GT-R', 'Juke', 'Kicks', 'Leaf', 'Maxima', 'Murano', 'NV', 'Pathfinder', 'Quest', 'Rogue', 'Rogue Sport', 'Sentra', 'Titan', 'Versa', 'Xterra', 'Z'],
  'Oldsmobile': ['Alero', 'Aurora', 'Bravada', 'Cutlass', 'Intrigue', 'Silhouette'],
  'Plymouth': ['Breeze', 'Neon', 'Prowler', 'Voyager'],
  'Pontiac': ['Aztek', 'Bonneville', 'Firebird', 'G5', 'G6', 'G8', 'Grand Am', 'Grand Prix', 'GTO', 'Montana', 'Solstice', 'Sunfire', 'Vibe'],
  'Porsche': ['718 Boxster', '718 Cayman', '911', 'Cayenne', 'Macan', 'Panamera', 'Taycan'],
  'Ram': ['1500', '2500', '3500', 'ProMaster'],
  'Rivian': ['R1S', 'R1T', 'R2'],
  'Rolls-Royce': ['Cullinan', 'Dawn', 'Ghost', 'Phantom', 'Spectre', 'Wraith'],
  'Saab': ['9-2X', '9-3', '9-5', '9-7X'],
  'Saturn': ['Astra', 'Aura', 'Ion', 'L-Series', 'Outlook', 'Relay', 'S-Series', 'Sky', 'Vue'],
  'Scion': ['FR-S', 'iA', 'iM', 'tC', 'xB', 'xD'],
  'Smart': ['EQ fortwo', 'fortwo'],
  'Subaru': ['Ascent', 'BRZ', 'Crosstrek', 'Forester', 'Impreza', 'Legacy', 'Outback', 'Solterra', 'WRX'],
  'Suzuki': ['Equator', 'Grand Vitara', 'Kizashi', 'SX4', 'Vitara', 'XL7'],
  'Tesla': ['Cybertruck', 'Model 3', 'Model S', 'Model X', 'Model Y', 'Roadster'],
  'Toyota': ['4Runner', '86', 'Avalon', 'C-HR', 'Camry', 'Celica', 'Corolla', 'Corolla Cross', 'Crown', 'FJ Cruiser', 'GR86', 'GR Corolla', 'GR Supra', 'Grand Highlander', 'Highlander', 'Land Cruiser', 'Matrix', 'Mirai', 'MR2', 'Prius', 'RAV4', 'Sequoia', 'Sienna', 'Solara', 'Supra', 'Tacoma', 'Tundra', 'Venza', 'Yaris', 'bZ4X'],
  'Volkswagen': ['Arteon', 'Atlas', 'Atlas Cross Sport', 'Beetle', 'CC', 'Eos', 'Golf', 'Golf GTI', 'Golf R', 'ID.4', 'ID.Buzz', 'Jetta', 'Jetta GLI', 'Passat', 'Phaeton', 'Rabbit', 'Taos', 'Tiguan', 'Touareg'],
  'Volvo': ['C30', 'C40', 'C70', 'EX30', 'EX90', 'S40', 'S60', 'S80', 'S90', 'V60', 'V90', 'XC40', 'XC60', 'XC90'],
  'Other': ['Other']
};

const parseBookingDateTime = (dateStr, timeStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  return new Date(year, month - 1, day, hours, minutes);
};

const downloadICS = (formData) => {
  const start = parseBookingDateTime(formData.date, formData.time);
  if (!start) return;
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:PDX Window Tint - ${formData.service}`,
    `DESCRIPTION:Service: ${formData.service}\\nDeposit: $${getDepositAmount(formData.service)} via Stripe\\nDROP-OFF ONLY\\nCall 971.249.2752`,
    `LOCATION:17960 SW Alvord Lane, Beaverton, OR`,
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'pdx-window-tint-appointment.ics';
  a.click();
};

const businessHours = {
  weekday: ['4:00 PM', '5:30 PM', '7:00 PM'],
  weekend: ['11:00 AM', '12:00 PM', '1:00 PM', '4:00 PM', '5:30 PM', '7:00 PM']
};

// Returns the day-of-month for the Nth occurrence of a weekday in a given month.
// n > 0 → nth from start (1=first, 2=second…), n < 0 → nth from end (-1=last)
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  if (n > 0) {
    const first = new Date(year, month, 1);
    const diff = (weekday - first.getDay() + 7) % 7;
    return 1 + diff + (n - 1) * 7;
  }
  const last = new Date(year, month + 1, 0);
  const diff = (last.getDay() - weekday + 7) % 7;
  return last.getDate() - diff;
};

// Returns true if dateStr (YYYY-MM-DD) is a US federal holiday or its observed date.
const isFederalHoliday = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const m = month; // 1-based
  const d = day;

  // Fixed-date holidays — also check observed dates (Sat→Fri, Sun→Mon)
  const fixed = [
    [1,  1],  // New Year's Day
    [6,  19], // Juneteenth
    [7,  4],  // Independence Day
    [11, 11], // Veterans Day
    [12, 25], // Christmas Day
  ];
  for (const [hm, hd] of fixed) {
    const actual = new Date(year, hm - 1, hd);
    const dow = actual.getDay();
    if (m === hm && d === hd) return true;                          // actual date
    if (dow === 6 && m === hm && d === hd - 1) return true;         // observed Friday
    if (dow === 0) {
      const obs = new Date(year, hm - 1, hd + 1);
      if (m === obs.getMonth() + 1 && d === obs.getDate()) return true; // observed Monday
    }
  }

  // Floating holidays
  const floating = [
    [0,  1,  3],  // MLK Day:         3rd Monday of January
    [1,  1,  3],  // Presidents' Day: 3rd Monday of February
    [4,  1, -1],  // Memorial Day:    last Monday of May
    [8,  1,  1],  // Labor Day:       1st Monday of September
    [9,  1,  2],  // Columbus Day:    2nd Monday of October
    [10, 4,  4],  // Thanksgiving:    4th Thursday of November
  ];
  for (const [mo, wday, n] of floating) {
    const hd = nthWeekdayOfMonth(year, mo, wday, n);
    if (m === mo + 1 && d === hd) return true;
  }

  return false;
};

export const BookingSection = ({ preselected }) => {
  const [step, setStep] = useState(1);
  const modalScrollRef = React.useRef(null);

  // Lock body scroll while booking modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Scroll modal container to top on every step change — fixes "black screen"
  // caused by overflow-y scroll position surviving a step transition
  useEffect(() => {
    if (modalScrollRef.current) {
      modalScrollRef.current.scrollTop = 0;
    }
  }, [step]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [adminBlockedDates, setAdminBlockedDates] = useState([]);
  const [discountActive, setDiscountActive] = useState(false);
  const [depositRequired, setDepositRequired] = useState(true);
  const [customTimeSlots, setCustomTimeSlots] = useState([]);
  const [pdxTimeSlots, setPdxTimeSlots] = useState([]); // authoritative slot list from PDXTINT app
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [showStarlightPicker, setShowStarlightPicker] = useState(false);
  const [showWindshieldAddon, setShowWindshieldAddon] = useState(false);
  const [showTintRemoval, setShowTintRemoval] = useState(false);
  const [showTintShade, setShowTintShade] = useState(false);
  const [shadeVehicleType, setShadeVehicleType] = useState('car');
  const [frontsVlt, setFrontsVlt] = useState(0);
  const [rearsVlt, setRearsVlt] = useState(0);
  const [windVlt, setWindVlt] = useState(0);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [tintTier, setTintTier] = useState('premium');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: preselected?.service || '',
    package: preselected?.package || '',
    starlightPackage: '',
    addWindshield: '',
    addTintRemoval: '',
    tintShade: '',
    date: '',
    time: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    message: '',
    manualPaymentMethod: '',
    paymentConfirmed: false
  });
  const [stripePaymentSuccess, setStripePaymentSuccess] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [stripeIntentLoading, setStripeIntentLoading] = useState(false);
  const [stripeIntentError, setStripeIntentError] = useState(null);
  const [paymentMethodChoice, setPaymentMethodChoice] = useState(null); // null | 'card' | 'apple_pay'
  const [blockedDateMsg, setBlockedDateMsg] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Checkout flow state
  const [checkoutAmountType, setCheckoutAmountType] = useState('deposit'); // 'deposit' | 'full'
  const [checkoutPayMethod, setCheckoutPayMethod] = useState('card');     // 'card' | 'finance'
  const [payMethodRow, setPayMethodRow] = useState('card'); // 'apple_pay' | 'card' | 'finance'
  const [bnplProvider, setBnplProvider] = useState(null); // null | 'affirm' | 'klarna' | 'afterpay'
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [bnplLoading, setBnplLoading] = useState(false);
  const scrollToBooking = () => {
    setTimeout(() => {
      const el = document.querySelector('#booking');
      if (el) {
        const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: pos, behavior: 'instant' });
      }
    }, 50);
  };

  const selectService = (value) => {
    handleInputChange('service', value);
    if (NO_PACKAGE_SERVICES.includes(value)) {
      handleInputChange('package', '');
      handleInputChange('tintShade', '');
      handleInputChange('addWindshield', '');
      handleInputChange('addTintRemoval', '');
      handleInputChange('starlightPackage', '');
    }
    setShowServicePicker(false);
  };

  // Determine which field should glow next (first empty required field)
  // Order must match form layout top-to-bottom
  const glowField = (() => {
    if (step !== 1) return '';
    // Service/package first (moved to top of form)
    if (!preselected && !formData.service) return 'service';
    if (!preselected && !NO_PACKAGE_SERVICES.includes(formData.service) && !formData.package) return 'package';
    // Remove Old Tint — required
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && !formData.addTintRemoval) return 'addTintRemoval';
    // Add Windshield — required for non-full non-windshield-only packages
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && showWindshieldField(formData.package) && !formData.addWindshield) return 'addWindshield';
    // Personal info (moved below service choices)
    if (!formData.name) return 'name';
    if (!formData.phone) return 'phone';
    if (!formData.email) return 'email';
    // Choose Shade
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && !formData.tintShade) return 'tintShade';
    if (!formData.date) return 'date';
    if (!formData.time) return 'time';
    if (!formData.vehicleYear) return 'vehicleYear';
    if (!formData.vehicleMake) return 'vehicleMake';
    if (!formData.vehicleModel) return 'vehicleModel';
    return '';
  })();

  const glowClass = '!border-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.3)] !ring-1 !ring-amber-500/40';
  const normalClass = '!border-zinc-900';

  // Restore form data after Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_success') === 'true') {
      const saved = localStorage.getItem('pdx_booking_form');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          parsed.manualPaymentMethod = 'Stripe';
          parsed.paymentConfirmed = true;
          setFormData(parsed);
          setStripePaymentSuccess(true);
          localStorage.removeItem('pdx_booking_form');
          window.history.replaceState({}, '', window.location.pathname);
          scrollToBooking();
          // Auto-submit booking after Stripe payment
          setStep(2);
        } catch (e) {
          console.error('Failed to restore form data', e);
        }
      }
    }
    if (params.get('payment_cancelled') === 'true') {
      const saved = localStorage.getItem('pdx_booking_form');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData(parsed);
          setStep(2);
          localStorage.removeItem('pdx_booking_form');
          window.history.replaceState({}, '', window.location.pathname);
          scrollToBooking();
          toast.error('Payment was cancelled. Please try again.');
        } catch (e) {
          console.error('Failed to restore form data', e);
        }
      }
    }
    // BNPL (Finance) return — Affirm / Klarna / Afterpay
    if (params.get('payment') === 'bnpl_success') {
      const sessionId = params.get('session_id');
      const saved = localStorage.getItem('pdx_booking_form');
      if (sessionId && saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData({
            name: parsed.name || '',
            phone: parsed.phone || '',
            email: parsed.email || '',
            service: parsed.service || '',
            package: parsed.package || '',
            starlightPackage: parsed.starlightPackage || '',
            addWindshield: parsed.addWindshield || '',
            addTintRemoval: parsed.addTintRemoval || '',
            tintShade: parsed.tintShade || '',
            date: parsed.date || '',
            time: parsed.time || '',
            vehicleYear: parsed.vehicleYear || '',
            vehicleMake: parsed.vehicleMake || '',
            vehicleModel: parsed.vehicleModel || '',
            message: parsed.message || '',
            manualPaymentMethod: 'Finance',
            paymentConfirmed: true,
          });
          window.history.replaceState({}, '', window.location.pathname);
          const vehicleInfo = [parsed.vehicleYear, parsed.vehicleMake, parsed.vehicleModel].filter(Boolean).join(' ');
          const computedTotal = getServiceTotalPrice(parsed.service, parsed.package, parsed.starlightPackage);
          axios.post(`${BACKEND_URL}/api/payments/stripe/verify-session`, {
            session_id: sessionId,
            booking_data: {
              name: parsed.name, email: parsed.email, phone: parsed.phone,
              service: parsed.service, package: parsed.package || '',
              date: parsed.date, time: parsed.time,
              vehicle_info: vehicleInfo,
              tint_shade: parsed.tintShade || '',
              message: parsed.message || '',
              total_price: computedTotal ? String(computedTotal) : '',
            },
          }).then(res => {
            if (res.data.ok) {
              localStorage.removeItem('pdx_booking_form');
              setStripePaymentSuccess(true);
              setStep(3);
              scrollToBooking();
            }
          }).catch(() => {
            toast.error('Could not verify financing payment. Please contact us with your receipt.');
          });
        } catch (e) {
          console.error('Failed to restore BNPL form data', e);
        }
      }
    }
    if (params.get('payment') === 'bnpl_cancelled') {
      toast.error('Financing was cancelled. You can choose a different payment method.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch blocked dates from BOTH shared PDXTINT API and local admin, discount status, deposit setting, and custom time slots
  useEffect(() => {
    const fetchBlockedDates = async () => {
      let allBlocked = [];
      // Use the backend-merged endpoint (combines local + PDXTINT, never calls Render.com directly)
      try {
        const mergedRes = await axios.get(`${BACKEND_URL}/api/sync/blocked-dates`);
        if (Array.isArray(mergedRes.data)) allBlocked = mergedRes.data;
      } catch (e) {
        // fallback: local only
        try {
          const localRes = await axios.get(`${BACKEND_URL}/api/blocked-dates`);
          if (Array.isArray(localRes.data)) allBlocked = [...localRes.data];
        } catch (e2) {}
      }
      setAdminBlockedDates(allBlocked);
    };
    fetchBlockedDates();
    axios.get(`${BACKEND_URL}/api/settings/discount`).then(res => setDiscountActive(res.data.active)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/settings/deposit`).then(res => setDepositRequired(res.data.required)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/custom-time-slots`).then(res => setCustomTimeSlots(res.data)).catch(() => {});
  }, []);

  const fetchBookedSlots = useCallback(async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    setPdxTimeSlots([]); // reset while loading
    try {
      const parts = date.split('-');
      const pdxDate = `${parts[1]}/${parts[2]}/${parts[0]}`; // MM/DD/YYYY

      // Route ALL time-slot queries through local backend (prevents silent timeout when PDXTINT sleeps on Render)
      const [pdxSlotsRes, localRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/time-slots?date=${date}`),
        axios.get(`${BACKEND_URL}/api/bookings/booked-slots?date=${date}`),
      ]);

      const bookedSet = new Set();
      const pdxList = [];

      // PDXTINT time-slots — authoritative source of truth
      if (pdxSlotsRes.status === 'fulfilled') {
        const slots = pdxSlotsRes.value.data || [];
        // If backend returns empty array it means the day is fully blocked — sentinel marker
        if (slots.length === 0 && pdxSlotsRes.value.status === 200) {
          // Day is blocked: mark a sentinel so getAvailableTimeSlots returns []
          setPdxTimeSlots(['__BLOCKED__']);
          setBookedSlots([]);
          setLoadingSlots(false);
          return;
        }
        slots.forEach(s => {
          if (s.time) {
            pdxList.push(s.time);
            if (!s.available) bookedSet.add(s.time);
          }
        });
      }

      // Local website bookings (supplement PDXTINT — catch bookings made on this site)
      if (localRes.status === 'fulfilled') {
        (localRes.value.data || []).forEach(s => s.time && bookedSet.add(s.time));
      }

      if (pdxList.length > 0) setPdxTimeSlots(pdxList);
      setBookedSlots([...bookedSet]);
    } catch {
      setBookedSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  const handleInputChange = (field, value) => {
    // When package changes: clear addWindshield + tintShade + internal VLTs
    if (field === 'package') {
      setFrontsVlt(0); setRearsVlt(0); setWindVlt(0);
      setFormData(prev => ({ ...prev, [field]: value, addWindshield: '', tintShade: '' }));
      return;
    }
    // When addWindshield → 'none': clear wind shade row
    if (field === 'addWindshield' && value === 'none') {
      setWindVlt(0);
      setFormData(prev => {
        const newShade = (prev.tintShade || '').split(', ').filter(p => !p.startsWith('Wind ')).join(', ');
        return { ...prev, [field]: value, tintShade: newShade };
      });
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'date') {
      setFormData(prev => ({ ...prev, [field]: value, time: '' }));
      fetchBookedSlots(value);
    }
  };

  useEffect(() => {
    if (formData.date) {
      fetchBookedSlots(formData.date);
    }
  }, [formData.date, fetchBookedSlots]);

  // Create a Stripe PaymentIntent when user clicks "Pay" with card method
  useEffect(() => {
    if (!showStripeForm || checkoutPayMethod !== 'card') return;
    if (stripeClientSecret || stripePaymentSuccess) return;
    if (!depositRequired) return;

    const vehicleInfo = [formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ');
    const totalPriceVal = getServiceTotalPrice(formData.service, formData.package, formData.starlightPackage);
    const chargeAmt = checkoutAmountType === 'full' && totalPriceVal
      ? totalPriceVal
      : getDepositAmount(formData.service);

    setStripeIntentLoading(true);
    setStripeIntentError(null);
    axios.post(`${BACKEND_URL}/api/payments/stripe/create-intent`, {
      service: formData.service,
      package: formData.package || '',
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      date: formData.date,
      time: formData.time,
      vehicle_info: vehicleInfo,
      tint_shade: formData.tintShade || '',
      message: formData.message || '',
      windshield_addon: formData.addWindshield === 'Yes',
      total_price: totalPriceVal ? String(totalPriceVal) : '',
      charge_amount: chargeAmt,
    })
      .then(res => {
        setStripeClientSecret(res.data.client_secret);
        setStripePaymentIntentId(res.data.payment_intent_id);
      })
      .catch((err) => {
        console.error('create-intent error:', err?.response?.data || err?.message || err);
        setStripeIntentError('Unable to initialize payment. Please go back and try again.');
      })
      .finally(() => setStripeIntentLoading(false));
  }, [showStripeForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAvailableTimeSlots = () => {
    if (!formData.date) return [];
    // Sentinel: backend returned [] meaning this day is fully blocked
    if (pdxTimeSlots.length === 1 && pdxTimeSlots[0] === '__BLOCKED__') return [];
    const extraSlots = customTimeSlots.filter(s => s.date === formData.date).map(s => s.time);

    // Use PDXTINT's authoritative slot list when available; fall back to hardcoded business hours
    const baseSlots = pdxTimeSlots.length > 0
      ? pdxTimeSlots
      : (() => {
          const parts = formData.date.split('-');
          const selectedDate = new Date(parts[0], parts[1] - 1, parts[2]);
          const dayOfWeek = selectedDate.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const isHoliday = isFederalHoliday(formData.date);
          return (isWeekend || isHoliday) ? businessHours.weekend : businessHours.weekday;
        })();

    const allSlots = [...new Set([...baseSlots, ...extraSlots])];
    // Sort by time
    allSlots.sort((a, b) => {
      const toMin = (t) => { const [time, period] = t.split(' '); let [h, m] = time.split(':').map(Number); if (period === 'PM' && h !== 12) h += 12; if (period === 'AM' && h === 12) h = 0; return h * 60 + m; };
      return toMin(a) - toMin(b);
    });
    return allSlots.filter(slot => !isTimeBlocked(formData.date, slot));
  };

  const isSlotBooked = (time) => bookedSlots.includes(time) || isTimeBlocked(formData.date, time);

  const handleStripePaymentSuccess = async (paymentIntentId) => {
    try {
      const vehicleInfo = [formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ');
      const totalPriceVal = getServiceTotalPrice(formData.service, formData.package, formData.starlightPackage);
      await axios.post(`${BACKEND_URL}/api/payments/stripe/confirm`, {
        payment_intent_id: paymentIntentId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        package: formData.package || '',
        date: formData.date,
        time: formData.time,
        vehicle_info: vehicleInfo,
        tint_shade: formData.tintShade || '',
        message: formData.message || '',
        windshield_addon: formData.addWindshield === 'Yes',
        total_price: totalPriceVal ? String(totalPriceVal) : '',
      });
      setStripePaymentSuccess(true);
      setStep(3);
      scrollToBooking();
    } catch {
      toast.error('Payment succeeded but booking confirmation failed. Please contact us.');
    }
  };

  // Finance (BNPL) — redirect to Stripe Checkout Session
  const handlePayNow = async () => {
    if (checkoutPayMethod === 'finance') {
      setBnplLoading(true);
      try {
        const vehicleInfo = [formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ');
        const totalPriceVal = getServiceTotalPrice(formData.service, formData.package, formData.starlightPackage);
        const chargeAmt = totalPriceVal || getDepositAmount(formData.service);
        const res = await axios.post(`${BACKEND_URL}/api/payments/stripe/checkout-session`, {
          amount: chargeAmt,
          service: formData.service,
          package: formData.package || '',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          date: formData.date,
          time: formData.time,
          vehicle_info: vehicleInfo,
          tint_shade: formData.tintShade || '',
          bnpl_provider: bnplProvider,
        });
        // Save form data to localStorage before redirect (for return restoration)
        localStorage.setItem('pdx_booking_form', JSON.stringify({ ...formData, vehicle_info: vehicleInfo }));
        window.location.href = res.data.checkout_url;
      } catch {
        toast.error('Failed to initialize financing. Please try again.');
        setBnplLoading(false);
      }
    } else {
      // Card/Apple Pay — show inline Stripe form
      setShowStripeForm(true);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.service || !formData.date || !formData.time || !formData.vehicleYear || !formData.vehicleMake || !formData.vehicleModel) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !formData.package) {
      toast.error('Please select a package');
      return;
    }
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && !formData.addTintRemoval) {
      toast.error('Please select a tint removal option');
      return;
    }
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && showWindshieldField(formData.package) && !formData.addWindshield) {
      toast.error('Please select a windshield option');
      return;
    }
    if (!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && !formData.tintShade) {
      toast.error('Please choose a shade');
      return;
    }
    // Always route to payment step — booking is only created after customer pays or explicitly confirms
    setStep(2);
    scrollToBooking();
  };

  // Explicit booking confirmation for no-deposit services — only runs when customer clicks "Confirm Appointment"
  const handleConfirmFreeBooking = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        service: formData.service,
        package: (formData.package + (formData.addWindshield === 'yes' ? ' + Front Windshield' : '') + (formData.addTintRemoval === 'yes' ? ' + Tint Removal' : '')) || formData.starlightPackage || '',
        tint_shade: formData.tintShade,
        date: formData.date,
        time: formData.time,
        vehicle_info: `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}`,
        message: formData.message,
        payment_method: 'N/A',
        payment_confirmed: false,
      });
      setStep(3);
      scrollToBooking();
      toast.success('Booking submitted successfully!');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('This time slot was just booked. Please go back and select a different time.');
        setStep(1);
      } else {
        toast.error('Failed to submit booking. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      package: '',
      starlightPackage: '',
      addWindshield: '',
      addTintRemoval: '',
      tintShade: '',
      date: '',
      time: '',
      vehicleYear: '',
      vehicleMake: '',
      vehicleModel: '',
      message: '',
      manualPaymentMethod: '',
      paymentConfirmed: false
    });
    setStep(1);
    setIsSubmitting(false);
    setBookedSlots([]);
    setPaymentMethodChoice(null);
    setStripeClientSecret(null);
    setStripeIntentError(null);
    setCheckoutAmountType('deposit');
    setCheckoutPayMethod('card');
    setShowStripeForm(false);
    setBnplProvider(null);
    setBnplLoading(false);
    setStripePaymentSuccess(false);
  };

  // Reusable modal picker for dropdowns (Navy cinematic style)
  const OptionModal = ({ show, onClose, title, subtitle, children }) => {
    if (!show) return null;
    return createPortal(
      <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2" style={{zIndex: 9999}} onClick={onClose}>
        <div className="bg-zinc-950 border border-zinc-800/40 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-black/60" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <h3 className="text-white text-2xl font-bold">{title}</h3>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center hover:bg-zinc-600 transition-colors flex-shrink-0">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="px-3 pb-3">
            {children}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Trigger button that opens the modal (styled like the current selects)
  const ModalTrigger = ({ testId, value, placeholder, onClick, glowOn }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 h-10 rounded-md bg-zinc-950 text-sm transition-all duration-500 border ${
        glowOn ? glowClass : normalClass
      } ${value ? 'text-white' : 'text-zinc-500'}`}
    >
      <span className="truncate">{value || placeholder}</span>
      <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
    </button>
  );

  return createPortal(
    <div ref={modalScrollRef} className="fixed inset-0 bg-black flex items-start justify-center overflow-y-auto" style={{zIndex: 9998}} data-testid="booking-overlay">
      <div className="w-full max-w-xl mx-auto px-4 sm:px-5 py-6 relative">
        {/* Close button */}
        <button
          onClick={() => { resetForm(); window.dispatchEvent(new CustomEvent('close-booking')); }}
          data-testid="close-booking-modal"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-800/80 flex items-center justify-center hover:bg-zinc-700 transition-colors z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Section Header */}
        <div className="mb-4">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-white tracking-tight mt-3">
            {step === 2 ? "LET'S FINALIZE" : "GREAT CHOICE!"}
          </h2>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-center">
            {[
              { num: 1, label: 'Details' },
              { num: 2, label: 'Payment' },
              { num: 3, label: 'Done' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                {i > 0 && (
                  <div className={`flex-1 h-0.5 mx-2 max-w-[60px] ${step >= s.num ? 'bg-amber-500' : 'bg-zinc-700'}`} />
                )}
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    data-testid={`step-indicator-${s.num}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      step >= s.num
                        ? 'bg-amber-500 text-black'
                        : 'bg-zinc-800 border-2 border-zinc-700 text-zinc-500'
                    }`}
                  >
                    {step > s.num
                      ? <Check className="w-5 h-5 text-black" strokeWidth={3} />
                      : s.num}
                  </div>
                  <span className={`text-xs font-semibold ${step >= s.num ? 'text-amber-500' : 'text-zinc-500'}`}>
                    {s.label}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-2xl">
          <div className="p-4 sm:p-6">
            {/* Step 1: Details */}
            {step === 1 && (
              <>
              <form onSubmit={handleStep1Submit} className="space-y-4" data-testid="booking-form-step1">
                {discountActive && (
                  <div data-testid="discount-banner" className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center animate-pulse">
                    <p className="text-green-400 font-bold text-lg tracking-wide">25% OFF — Limited Time Only!</p>
                  </div>
                )}

                {/* ── 1. Selected Service Pill (centered) ── */}
                {preselected ? (
                  <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
                    <div className="flex-1 text-center">
                      <p className="text-zinc-400 text-xs uppercase tracking-widest mb-0.5">Selected Service</p>
                      <p className="text-amber-400 font-bold text-sm">
                        {formData.service}{formData.package ? ` — ${formData.package}` : ''}
                      </p>
                    </div>
                    <button type="button" onClick={() => setShowPackagePicker(true)}
                      className="text-zinc-500 text-xs hover:text-zinc-300 underline underline-offset-2 flex-shrink-0 ml-3">
                      Change
                    </button>
                  </div>
                ) : (
                    <>
                      <div>
                        <Label htmlFor="service" className="text-white mb-2">Service Type *</Label>
                        <button
                          type="button"
                          data-testid="select-service"
                          onClick={() => setShowServicePicker(true)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md bg-zinc-950 text-sm transition-all duration-500 ${
                            glowField === 'service' ? glowClass : normalClass
                          } ${formData.service ? 'text-white' : 'text-zinc-500'}`}
                        >
                          <span>{formData.service || 'Select a service'}</span>
                          <ChevronDown className="w-4 h-4 text-zinc-500" />
                        </button>
                      </div>

                      {/* Service Picker Modal — 2x2 Grid (Navy Style) */}
                      {showServicePicker && createPortal(
                        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}} onClick={() => setShowServicePicker(false)}>
                          <div className="bg-zinc-950 border border-zinc-800/40 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-black/60" onClick={e => e.stopPropagation()}>
                            <div className="flex items-start justify-between px-6 pt-6 pb-2">
                              <div>
                                <h3 className="text-amber-500 text-2xl font-bold">Service</h3>
                                <p className="text-zinc-500 text-sm mt-0.5">Select a service</p>
                              </div>
                              <button data-testid="close-service-picker" onClick={() => setShowServicePicker(false)} className="w-9 h-9 rounded-full bg-zinc-700/60 flex items-center justify-center hover:bg-zinc-600 transition-colors mt-1">
                                <X className="w-5 h-5 text-white" />
                              </button>
                            </div>
                            <div className="px-5 pb-6 pt-3 grid grid-cols-2 gap-3">
                              {services.map((svc) => (
                                <button
                                  key={svc.id}
                                  data-testid={`service-option-${svc.title.toLowerCase().replace(/\s+/g, '-')}`}
                                  onClick={() => selectService(svc.title)}
                                  className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                                    formData.service === svc.title
                                      ? 'bg-amber-500 border-amber-400 text-black'
                                      : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                                  }`}
                                >
                                  <span className="block font-bold text-sm">{svc.title}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                      {!NO_PACKAGE_SERVICES.includes(formData.service) && (
                        <div>
                          <Label htmlFor="package" className="text-white mb-2">Package *</Label>
                          <ModalTrigger
                            testId="select-package"
                            value={formData.package}
                            placeholder="Select a package"
                            onClick={() => setShowPackagePicker(true)}
                            glowOn={glowField === 'package'}
                          />
                        </div>
                      )}
                    </>
                  )}

                {/* ── Choose Shade FIRST (only for tint services) — "Start here" ── */}
                {!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && (
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="tintShade" className="text-amber-400 font-semibold">Choose Shade *</Label>
                      <span className="text-xs text-amber-500/70 animate-pulse">Start here</span>
                    </div>
                    <button
                      type="button"
                      data-testid="select-tint"
                      onClick={() => {
                        setFrontsVlt(parseSavedShade(formData.tintShade, 'Fronts'));
                        setRearsVlt(parseSavedShade(formData.tintShade, 'Rears'));
                        setWindVlt(parseSavedShade(formData.tintShade, 'Wind'));
                        setShowTintShade(true);
                      }}
                      className={`w-full flex items-center justify-between px-3 h-10 rounded-md bg-zinc-950 text-sm transition-all duration-500 border ${
                        glowField === 'tintShade' ? glowClass : '!border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.2)]'
                      } ${formData.tintShade ? 'text-white' : 'text-zinc-500'}`}
                    >
                      <span className="truncate">{formData.tintShade || 'Choose your shade'}</span>
                      <ChevronDown className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    </button>
                  </div>
                )}

                {/* Starlight Package field hidden — package already chosen on service page */}


                {/* ── 4. Full Name + Phone ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="John Doe"
                      className={`bg-zinc-950 text-white transition-all duration-500 ${glowField === 'name' ? glowClass : normalClass}`}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white mb-2">Phone Number *</Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="971-XXX-XXXX"
                      className={`bg-zinc-950 text-white transition-all duration-500 ${glowField === 'phone' ? glowClass : normalClass}`}
                      required
                    />
                  </div>
                </div>

                {/* ── 5. Email ── */}
                <div>
                  <Label htmlFor="email" className="text-white mb-2">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="you@example.com"
                    className={`bg-zinc-950 text-white transition-all duration-500 ${glowField === 'email' ? glowClass : normalClass}`}
                    required
                  />
                </div>


                <div>
                  <Label className="text-white text-sm mb-2 block">Preferred Date &amp; Time *</Label>
                  <button
                    type="button"
                    data-testid="input-date-time"
                    onClick={() => setCalendarOpen(true)}
                    className={`w-full flex items-center justify-between px-3 h-10 rounded-md bg-zinc-950 text-sm transition-all duration-500 border ${
                      (glowField === 'date' || glowField === 'time') ? glowClass : normalClass
                    } ${(formData.date && formData.time) ? 'text-white' : 'text-zinc-500'}`}
                  >
                    <span className="truncate">
                      {formData.date && formData.time
                        ? `${new Date(formData.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${formData.time}`
                        : formData.date
                          ? `${new Date(formData.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} — pick a time`
                          : 'Select a date & time'}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  </button>
                  {blockedDateMsg && (
                    <p data-testid="blocked-date-msg" className="mt-2 text-sm text-red-400 font-medium bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      {blockedDateMsg}
                    </p>
                  )}
                </div>

                <div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white mb-2 block">Vehicle Year *</Label>
                      <Select value={formData.vehicleYear} onValueChange={(value) => handleInputChange('vehicleYear', value)}>
                        <SelectTrigger data-testid="select-vehicle-year" className={`bg-zinc-950 text-white h-12 text-base transition-all duration-500 ${glowField === 'vehicleYear' ? glowClass : normalClass}`}>
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-900 max-h-60" style={{zIndex: 99999}}>
                          {vehicleYears.map((y) => (
                            <SelectItem key={y} value={y} className="text-white">{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Vehicle Make *</Label>
                      <Select value={formData.vehicleMake} onValueChange={(value) => {
                        handleInputChange('vehicleMake', value);
                        handleInputChange('vehicleModel', '');
                      }}>
                        <SelectTrigger data-testid="select-vehicle-make" className={`bg-zinc-950 text-white h-12 text-base transition-all duration-500 ${glowField === 'vehicleMake' ? glowClass : normalClass}`}>
                          <SelectValue placeholder="Make" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-900 max-h-60" style={{zIndex: 99999}}>
                          {Object.keys(vehicleMakes).map((make) => (
                            <SelectItem key={make} value={make} className="text-white">{make}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Vehicle Model *</Label>
                      <Select value={formData.vehicleModel} onValueChange={(value) => handleInputChange('vehicleModel', value)} disabled={!formData.vehicleMake}>
                        <SelectTrigger data-testid="select-vehicle-model" className={`bg-zinc-950 text-white h-12 text-base transition-all duration-500 ${glowField === 'vehicleModel' ? glowClass : normalClass}`}>
                          <SelectValue placeholder="Model" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-900 max-h-60" style={{zIndex: 99999}}>
                          {(vehicleMakes[formData.vehicleMake] || []).map((model) => (
                            <SelectItem key={model} value={model} className="text-white">{model}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>


                {/* ── Remove Old Tint (LAST field, "Almost there") ── */}
                {!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="addTintRemoval" className="text-white">Remove Old Tint *</Label>
                      <span className="text-xs text-zinc-500 italic">Almost there</span>
                    </div>
                    <ModalTrigger
                      testId="select-tint-removal-addon"
                      value={formData.addTintRemoval === 'yes' ? 'Yes, add Tint Removal (+$100)' : formData.addTintRemoval === 'none' ? 'No thanks' : ''}
                      placeholder="Select an option"
                      onClick={() => setShowTintRemoval(true)}
                      glowOn={glowField === 'addTintRemoval'}
                    />
                  </div>
                )}

                {/* ── Add Windshield (right after Remove Old Tint for Front Two Windows) ── */}
                {!NO_PACKAGE_SERVICES.includes(formData.service) && !NO_TINT_SHADE_SERVICES.includes(formData.service) && showWindshieldField(formData.package) && (
                  <div>
                    <Label htmlFor="addWindshield" className="text-white mb-2">Add Windshield *</Label>
                    <ModalTrigger
                      testId="select-windshield-addon"
                      value={formData.addWindshield === 'yes' ? 'Yes, add Front Windshield (+$100)' : formData.addWindshield === 'none' ? 'No thanks' : ''}
                      placeholder="Select an option"
                      onClick={() => setShowWindshieldAddon(true)}
                      glowOn={glowField === 'addWindshield'}
                    />
                  </div>
                )}

                {/* ── Chrome Delete full-day notice (bottom) ── */}
                {formData.service === 'Chrome Delete' && (
                  <div className="border border-amber-500/40 bg-[#0a0d1a] rounded-2xl p-4 text-center">
                    <p className="text-amber-400 font-bold text-sm mb-1">Important Notice</p>
                    <p className="text-zinc-300 text-sm">Chrome Delete is a full day service. Vehicle needs to be dropped off and picked up the next day.</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="message" className="text-white mb-2">Additional Notes</Label>
                  <Textarea
                    id="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Any specific requirements or questions?"
                    className="bg-zinc-950 border-zinc-900 text-white min-h-[100px]"
                  />
                </div>

                {depositRequired && needsDeposit(formData.service, formData.package) && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4" data-testid="deposit-notice">
                    <p className="text-sm text-amber-400 text-center font-medium">
                      A <strong>${getDepositAmount(formData.service)} deposit</strong> is required to confirm your appointment.
                    </p>
                  </div>
                )}

                <Button 
                  type="submit"
                  data-testid="continue-to-payment-btn"
                  className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide text-sm"
                >
                  {depositRequired ? 'Continue to Payment' : 'Review & Confirm'}
                </Button>
              </form>

              {/* Package Picker Modal — with Tier Tabs for Window Tinting */}
              <OptionModal show={showPackagePicker} onClose={() => setShowPackagePicker(false)} title={formData.service === 'Chrome Delete' ? 'Chrome Delete' : 'Select Package'} subtitle={formData.service === 'Chrome Delete' ? 'Select a package' : ''}>
                {formData.service !== 'Chrome Delete' ? (
                  <div className="space-y-2">
                    {/* ── Carbon Film ── */}
                    <div className="rounded-xl border border-zinc-700/70 p-2">
                      <p className="text-white text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Carbon Film</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { title: 'Sedan & Coupe', price: 200, tier: 'Premium Carbon', desc: 'Sides & Rear' },
                          { title: 'Sedan & Coupe Full', price: 300, tier: 'Premium Carbon', desc: 'All Windows' },
                          { title: 'Trucks & SUV', price: 300, tier: 'Premium Carbon', desc: 'Sides & Rear' },
                          { title: 'Trucks & SUV Full', price: 400, tier: 'Premium Carbon', desc: 'All Windows' },
                        ].map((pkg) => {
                          const selected = formData.package.startsWith(`${pkg.title} (${pkg.tier})`);
                          return (
                            <button
                              key={`${pkg.title}-${pkg.price}`}
                              data-testid={`package-option-${pkg.title.toLowerCase().replace(/\s+/g, '-')}-${pkg.price}`}
                              onClick={() => {
                                const val = `${pkg.title} (${pkg.tier}) - $${pkg.price}`;
                                handleInputChange('package', val);
                                if (!needsWindshieldAddon(val)) handleInputChange('addWindshield', '');
                                setShowPackagePicker(false);
                              }}
                              style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                              className={`rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full ${selected ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'}`}
                            >
                              <span className="block font-bold leading-tight" style={{ fontSize: '13px' }}>{pkg.title}</span>
                              <span className="block mt-0.5" style={{ fontSize: '10px', color: selected ? 'rgba(0,0,0,0.5)' : '#71717a' }}>{pkg.desc}</span>
                              <span className="block font-bold mt-0.5" style={{ fontSize: '16px', color: selected ? 'rgba(0,0,0,0.8)' : '#ffffff' }}>${pkg.price}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Ceramic Film ── */}
                    <div className="rounded-xl border border-amber-500/80 p-2">
                      <p className="text-amber-500 text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Ceramic Film</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { title: 'Sedan & Coupe', price: 300, tier: 'Ceramic', desc: 'Sides & Rear' },
                          { title: 'Sedan & Coupe Full', price: 400, tier: 'Ceramic', desc: 'All Windows' },
                          { title: 'Trucks & SUV', price: 400, tier: 'Ceramic', desc: 'Sides & Rear' },
                          { title: 'Trucks & SUV Full', price: 500, tier: 'Ceramic', desc: 'All Windows' },
                        ].map((pkg) => {
                          const selected = formData.package.startsWith(`${pkg.title} (${pkg.tier})`);
                          return (
                            <button
                              key={`${pkg.title}-${pkg.price}`}
                              data-testid={`package-option-${pkg.title.toLowerCase().replace(/\s+/g, '-')}-${pkg.price}`}
                              onClick={() => {
                                const val = `${pkg.title} (${pkg.tier}) - $${pkg.price}`;
                                handleInputChange('package', val);
                                if (!needsWindshieldAddon(val)) handleInputChange('addWindshield', '');
                                setShowPackagePicker(false);
                              }}
                              style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                              className={`rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full ${selected ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'}`}
                            >
                              <span className="block font-bold leading-tight" style={{ fontSize: '13px' }}>{pkg.title}</span>
                              <span className="block mt-0.5" style={{ fontSize: '10px', color: selected ? 'rgba(0,0,0,0.5)' : '#71717a' }}>{pkg.desc}</span>
                              <span className="block font-bold mt-0.5" style={{ fontSize: '16px', color: selected ? 'rgba(0,0,0,0.8)' : '#f59e0b' }}>${pkg.price}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Factory Match / Windshield ── */}
                    <div className="rounded-xl border border-zinc-700/70 p-2">
                      <p className="text-white text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Factory Match / Windshield</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { title: 'Front Two Windows', price: 100, tier: 'Premium Carbon', desc: 'Carbon - Driver & Pass.' },
                          { title: 'Front Windshield Only', price: 100, tier: 'Premium Carbon', desc: 'Carbon - Windshield' },
                          { title: 'Front Two Windows', price: 150, tier: 'Ceramic', desc: 'Ceramic - Driver & Pass.' },
                          { title: 'Front Windshield Only', price: 150, tier: 'Ceramic', desc: 'Ceramic - Windshield' },
                        ].map((pkg) => {
                          const selected = formData.package.startsWith(`${pkg.title} (${pkg.tier})`);
                          return (
                            <button
                              key={`${pkg.title}-${pkg.price}`}
                              data-testid={`package-option-${pkg.title.toLowerCase().replace(/\s+/g, '-')}-${pkg.price}`}
                              onClick={() => {
                                const val = `${pkg.title} (${pkg.tier}) - $${pkg.price}`;
                                handleInputChange('package', val);
                                if (!needsWindshieldAddon(val)) handleInputChange('addWindshield', '');
                                setShowPackagePicker(false);
                              }}
                              style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                              className={`rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full ${selected ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'}`}
                            >
                              <span className="block font-bold leading-tight" style={{ fontSize: '13px' }}>{pkg.title}</span>
                              <span className="block mt-0.5" style={{ fontSize: '10px', color: selected ? 'rgba(0,0,0,0.5)' : '#71717a' }}>{pkg.desc}</span>
                              <span className="block font-bold mt-0.5" style={{ fontSize: '16px', color: selected ? 'rgba(0,0,0,0.8)' : '#ffffff' }}>${pkg.price}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { title: 'Window Trim Delete', price: 200, desc: 'Both Sides' },
                      { title: 'Door Handles', price: 150, desc: '4 Doors' },
                      { title: 'Front & Rear Emblem', price: 100, desc: 'Badges' },
                      { title: 'Special Request', price: 'Contact Us', desc: 'Custom Work' },
                    ].map((pkg) => (
                      <button
                        key={pkg.title}
                        data-testid={`package-option-${pkg.title.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => {
                          const val = typeof pkg.price === 'number' ? `${pkg.title} - $${pkg.price}` : pkg.title;
                          handleInputChange('package', val);
                          setShowPackagePicker(false);
                        }}
                        className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                          formData.package.startsWith(pkg.title)
                            ? 'bg-amber-500 border-amber-400 text-black'
                            : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                        }`}
                      >
                        <span className="block font-bold text-sm leading-tight">{pkg.title}</span>
                        <span className={`block text-zinc-400 text-xs mt-1 ${formData.package.startsWith(pkg.title) ? '!text-black/60' : ''}`}>{pkg.desc}</span>
                        <span className={`block text-lg font-bold mt-1.5 ${formData.package.startsWith(pkg.title) ? 'text-black/70' : 'text-amber-500'}`}>
                          {typeof pkg.price === 'number' ? `$${pkg.price}` : pkg.price}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </OptionModal>

              {/* Starlight Package Picker Modal */}
              <OptionModal show={showStarlightPicker} onClose={() => setShowStarlightPicker(false)} title="Starlight" subtitle="Select a package">
                <div className="grid grid-cols-2 gap-3">
                  {STARLIGHT_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.value}
                      data-testid={`starlight-option-${pkg.value.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => { handleInputChange('starlightPackage', pkg.value); setShowStarlightPicker(false); }}
                      className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                        formData.starlightPackage === pkg.value
                          ? 'bg-amber-500 border-amber-400 text-black'
                          : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                      }`}
                    >
                      <span className="block font-bold text-sm leading-tight">{pkg.label}</span>
                    </button>
                  ))}
                </div>
              </OptionModal>

              {/* Windshield Addon Modal */}
              <OptionModal show={showWindshieldAddon} onClose={() => setShowWindshieldAddon(false)} title="Front Windshield" subtitle="Add-on option">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    data-testid="windshield-option-no"
                    onClick={() => { handleInputChange('addWindshield', 'none'); setShowWindshieldAddon(false); }}
                    className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                      formData.addWindshield === 'none' ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                    }`}
                  >
                    <span className="block font-bold text-sm">No thanks</span>
                  </button>
                  <button
                    data-testid="windshield-option-yes"
                    onClick={() => { handleInputChange('addWindshield', 'yes'); setShowWindshieldAddon(false); }}
                    className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                      formData.addWindshield === 'yes' ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                    }`}
                  >
                    <span className="block font-bold text-sm">Yes</span>
                    <span className={`block text-lg font-bold mt-1.5 ${formData.addWindshield === 'yes' ? 'text-black/70' : 'text-amber-500'}`}>+$100</span>
                  </button>
                </div>
              </OptionModal>

              {/* Tint Removal Addon Modal */}
              <OptionModal show={showTintRemoval} onClose={() => setShowTintRemoval(false)} title="Tint Removal" subtitle="Add-on option">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    data-testid="tint-removal-option-no"
                    onClick={() => { handleInputChange('addTintRemoval', 'none'); setShowTintRemoval(false); }}
                    className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                      formData.addTintRemoval === 'none' ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                    }`}
                  >
                    <span className="block font-bold text-sm">No thanks</span>
                  </button>
                  <button
                    data-testid="tint-removal-option-yes"
                    onClick={() => { handleInputChange('addTintRemoval', 'yes'); setShowTintRemoval(false); }}
                    className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                      formData.addTintRemoval === 'yes' ? 'bg-amber-500 border-amber-400 text-black' : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                    }`}
                  >
                    <span className="block font-bold text-sm">Yes</span>
                    <span className={`block text-lg font-bold mt-1.5 ${formData.addTintRemoval === 'yes' ? 'text-black/70' : 'text-amber-500'}`}>+$100</span>
                  </button>
                </div>
              </OptionModal>

              {/* Choose Shade Modal — reference-matched design */}
              {showTintShade && createPortal(
                (() => {
                  const pkg = formData.package;
                  const addWS = formData.addWindshield;
                  const hasFronts = showFrontsRow(pkg);
                  const hasRears = showRearsRow(pkg);
                  const hasWind = showWindRow(pkg, addWS);

                  const subtitle = isFrontTwoWindowsPkg(pkg)
                    ? 'Rear factory are pre-tinted at 20-25%'
                    : isFrontWindshieldOnlyPkg(pkg)
                    ? ''
                    : 'Please choose shade for each side';

                  // Overlays: factory 15% on hidden fronts/rears; CLEAR windshield when not selected
                  const frontsOvl = hasFronts ? (frontsVlt ? vltToImageCode(frontsVlt) : null) : '15';
                  const rearsOvl = hasRears ? (rearsVlt ? vltToImageCode(rearsVlt) : null) : '15';
                  const windOvl = hasWind ? (windVlt ? vltToImageCode(windVlt) : null) : null;
                  const hasBack = shadeVehicleType !== 'truck';
                  const allSelected = (!hasFronts || frontsVlt) && (!hasRears || rearsVlt) && (!hasWind || windVlt);

                  const handleConfirm = () => {
                    if (!allSelected) return;
                    handleInputChange('tintShade', buildTintShadeString(frontsVlt, rearsVlt, windVlt, pkg, addWS));
                    setShowTintShade(false);
                  };

                  const renderVltRow = (label, selected, onSelect, testPrefix) => (
                    <tr key={label}>
                      <td style={{ paddingRight: '10px', paddingBottom: '12px', color: '#eab308', fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{label}</td>
                      {VLT_OPTIONS.map(vlt => (
                        <td key={vlt} style={{ textAlign: 'center', paddingBottom: '12px', paddingLeft: '4px', paddingRight: '4px' }}>
                          <button
                            data-testid={`${testPrefix}-${vlt}`}
                            onClick={() => onSelect(selected === vlt ? 0 : vlt)}
                            style={{
                              width: '36px', height: '36px', borderRadius: '50%',
                              border: `2px solid ${selected === vlt ? '#eab308' : 'rgba(255,255,255,0.2)'}`,
                              background: selected === vlt ? '#eab308' : 'rgba(255,255,255,0.04)',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {selected === vlt && <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#000' }} />}
                          </button>
                        </td>
                      ))}
                    </tr>
                  );

                  return (
                    <div
                      data-testid="choose-shade-modal"
                      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#000000', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
                    >
                      {/* Header */}
                      <div style={{ padding: '18px 16px 10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
                        <div>
                          <h2 style={{ color: '#eab308', fontWeight: 900, fontSize: '26px', margin: 0, letterSpacing: '-0.01em' }}>Choose Shade</h2>
                          {subtitle && <p style={{ color: '#71717a', fontSize: '12px', marginTop: '4px' }}>{subtitle}</p>}
                        </div>
                        <button onClick={() => setShowTintShade(false)} data-testid="close-shade-modal" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginTop: '2px' }}>
                          <X className="w-5 h-5 text-white" />
                        </button>
                      </div>

                      {/* Car preview */}
                      <div style={{ background: '#f0f0f0', borderRadius: '14px', margin: '0 12px 14px', flexShrink: 0, height: '230px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(/images/tint/${shadeVehicleType}-white.png)`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                        {rearsOvl && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(/images/tint/${shadeVehicleType}-rears-${rearsOvl}.png)`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />}
                        {rearsOvl && hasBack && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(/images/tint/${shadeVehicleType}-back-${rearsOvl}.png)`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />}
                        {frontsOvl && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(/images/tint/${shadeVehicleType}-front-${frontsOvl}.png)`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />}
                        {windOvl && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(/images/tint/${shadeVehicleType}-windshield-${windOvl}.png)`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />}
                      </div>

                      {/* Vehicle type chips */}
                      <div style={{ padding: '0 12px 14px', flexShrink: 0 }}>
                        <p style={{ color: '#eab308', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '9px', textAlign: 'center' }}>VEHICLE TYPE</p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {[['car', 'Sedan'], ['suv', 'SUV'], ['truck', 'Truck']].map(([id, label]) => (
                            <button
                              key={id}
                              data-testid={`shade-vehicle-${id}`}
                              onClick={() => setShadeVehicleType(id)}
                              style={{
                                flex: 1, height: '44px', borderRadius: '12px', border: '1.5px solid',
                                borderColor: shadeVehicleType === id ? '#eab308' : 'rgba(255,255,255,0.12)',
                                background: shadeVehicleType === id ? 'rgba(234,179,8,0.15)' : 'rgba(255,255,255,0.05)',
                                color: shadeVehicleType === id ? '#eab308' : '#71717a',
                                fontWeight: 700, fontSize: '14px', cursor: 'pointer', transition: 'all 0.15s ease',
                              }}
                            >{label}</button>
                          ))}
                        </div>
                      </div>

                      {/* VLT Table — no flex:1, content drives height */}
                      <div style={{ padding: '0 12px 16px', flexShrink: 0 }}>
                        <p style={{ color: '#eab308', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>WINDOW TINT (VLT)</p>
                        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)', padding: '14px 10px 4px' }}>
                          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead>
                              <tr>
                                <th style={{ width: '52px' }} />
                                {VLT_OPTIONS.map(vlt => (
                                  <th key={vlt} style={{ textAlign: 'center', paddingBottom: '8px', color: '#eab308', fontWeight: 700, fontSize: '12px', paddingLeft: '4px', paddingRight: '4px' }}>
                                    {String(vlt).padStart(2, '0')}%
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {hasFronts && renderVltRow('Fronts', frontsVlt, setFrontsVlt, 'shade-fronts')}
                              {hasRears && renderVltRow('Rears', rearsVlt, setRearsVlt, 'shade-rears')}
                              {hasWind && renderVltRow('Wind.', windVlt, setWindVlt, 'shade-wind')}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Bottom actions — immediately below table, no gap */}
                      <div style={{ padding: '0 12px 28px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                          <button
                            data-testid="reset-shade-btn"
                            onClick={() => { setFrontsVlt(0); setRearsVlt(0); setWindVlt(0); }}
                            style={{ flex: 1, height: '52px', borderRadius: '14px', background: 'transparent', border: '1.5px solid #eab308', color: '#eab308', fontWeight: 700, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}
                          >
                            <RotateCcw className="w-4 h-4" />
                            Reset Tint
                          </button>
                          <button
                            data-testid="confirm-shade-btn"
                            onClick={handleConfirm}
                            disabled={!allSelected}
                            style={{ flex: 1, height: '52px', borderRadius: '14px', background: allSelected ? '#eab308' : 'rgba(234,179,8,0.55)', color: '#000', fontWeight: 800, fontSize: '14px', border: 'none', cursor: allSelected ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}
                          >
                            <Check className="w-4 h-4" />
                            Confirm
                          </button>
                        </div>
                        <a href="https://www.oregonlegislature.gov/bills_laws/ors/ors815.html" target="_blank" rel="noopener noreferrer" style={{ color: '#52525b', fontSize: '11px', textAlign: 'center', textDecoration: 'underline', display: 'block' }}>Please check your local tint laws</a>
                      </div>
                    </div>
                  );
                })(),
                document.body
              )}




              {/* Combined Date & Time Picker Modal */}
              <OptionModal show={calendarOpen} onClose={() => setCalendarOpen(false)} title="Pick Date & Time" subtitle="">
                <div className="space-y-5">
                  {/* Step 1: Calendar */}
                  <div>
                    <p className="text-white text-sm font-semibold mb-2">1. Pick a date</p>
                    <Calendar
                      mode="single"
                      selected={formData.date ? new Date(formData.date + 'T12:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const val = date.toISOString().split('T')[0];
                          if (isDateBlocked(val) || adminBlockedDates.includes(val)) {
                            setBlockedDateMsg('This date is unavailable. Please select another date.');
                            return;
                          }
                          setBlockedDateMsg('');
                          handleInputChange('date', val);
                          handleInputChange('time', '');
                        }
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        const val = date.toISOString().split('T')[0];
                        return isDateBlocked(val) || adminBlockedDates.includes(val);
                      }}
                      className="text-white w-full"
                      classNames={{
                        months: "flex flex-col w-full",
                        month: "space-y-3 w-full",
                        caption: "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-base font-bold text-white",
                        nav_button: "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-zinc-800 rounded-md inline-flex items-center justify-center",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "flex justify-between",
                        head_cell: "text-zinc-500 rounded-md flex-1 font-semibold text-sm text-center",
                        row: "flex w-full justify-between mt-1",
                        cell: "relative p-0 text-center flex-1 text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-amber-500/20 [&:has([aria-selected])]:rounded-md",
                        day: "h-11 w-full p-0 font-medium text-white hover:bg-zinc-800 rounded-md inline-flex items-center justify-center text-base",
                        day_selected: "bg-amber-500 text-black hover:bg-amber-400 focus:bg-amber-500 focus:text-black",
                        day_today: "bg-zinc-800 text-amber-500",
                        day_outside: "text-zinc-700",
                        day_disabled: "text-zinc-700 opacity-30",
                        day_hidden: "invisible",
                      }}
                    />
                  </div>

                  {/* Step 2: Time slots — shown after date is picked */}
                  {formData.date && (
                    <div>
                      <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                        2. Pick a time
                        {loadingSlots && <Loader2 className="w-3 h-3 animate-spin text-amber-500" />}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {getAvailableTimeSlots().map((time) => (
                          <button
                            key={time}
                            type="button"
                            data-testid={`time-option-${time.replace(/\s+/g, '-').toLowerCase()}`}
                            disabled={isSlotBooked(time)}
                            onClick={() => {
                              if (!isSlotBooked(time)) {
                                handleInputChange('time', time);
                                setCalendarOpen(false);
                              }
                            }}
                            className={`py-3 px-2 rounded-xl text-center transition-all duration-150 border text-sm font-bold ${
                              isSlotBooked(time)
                                ? 'bg-[#0d1424] border-[#1a2744]/30 text-zinc-600 line-through opacity-50 cursor-not-allowed'
                                : formData.time === time
                                  ? 'bg-amber-500 border-amber-400 text-black'
                                  : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                            }`}
                          >
                            {time}
                            {isSlotBooked(time) && <span className="block text-xs font-normal mt-0.5">Booked</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </OptionModal>
              </>
            )}

            {/* Step 2: Stripe Embedded Payment */}
            {step === 2 && (
              <div className="space-y-6" data-testid="booking-form-step2">
                {stripePaymentSuccess ? (
                  <div className="space-y-6">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center" data-testid="stripe-payment-success-banner">
                      <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                      </div>
                      <span className="text-green-400 font-bold text-2xl block mb-2">Payment Confirmed!</span>
                      <p className="text-zinc-400 text-sm">Your ${getDepositAmount(formData.service)} deposit was received. Your appointment is now confirmed.</p>
                    </div>

                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-3">
                      <h4 className="text-white font-semibold text-center mb-3">Booking Summary</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-zinc-500">Name</span><p className="text-white">{formData.name}</p></div>
                        <div><span className="text-zinc-500">Service</span><p className="text-white">{formData.service}</p></div>
                        {formData.package && <div><span className="text-zinc-500">Package</span><p className="text-white">{formData.package}</p></div>}
                        <div><span className="text-zinc-500">Date</span><p className="text-white">{formData.date}</p></div>
                        <div><span className="text-zinc-500">Time</span><p className="text-white">{formData.time}</p></div>
                        <div><span className="text-zinc-500">Vehicle</span><p className="text-white">{formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}</p></div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      data-testid="finalize-booking-btn"
                      onClick={() => { setStep(3); scrollToBooking(); }}
                      className="w-full h-14 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-wide text-base"
                    >
                      View Confirmation
                    </Button>
                  </div>
                ) : (
                  /* ── Stripe Payment Form OR No-Deposit Confirmation ── */
                  depositRequired ? (
                  /* ── 3-Step BNPL Checkout ── */
                  (() => {
                    const totalPrice = getServiceTotalPrice(formData.service, formData.package, formData.starlightPackage);
                    const payAmount = (checkoutPayMethod === 'finance' || checkoutAmountType === 'full')
                      ? (totalPrice || getDepositAmount(formData.service))
                      : getDepositAmount(formData.service);
                    return (
                  <div className="space-y-3" data-testid="bnpl-checkout">

                    {/* Back button */}
                    <button
                      type="button"
                      data-testid="back-to-details-btn"
                      onClick={() => { setStep(1); setShowStripeForm(false); setStripeClientSecret(null); setStripeIntentError(null); }}
                      className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm transition-colors mb-1"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      Back to Details
                    </button>

                    {/* ── Card 1: Choose Amount ── */}
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <span className="text-white font-bold">Choose your amount</span>
                      </div>
                      <div className="px-5 pb-5 space-y-2.5">
                        {/* Deposit option */}
                        <button
                          type="button"
                          data-testid="checkout-amount-deposit"
                          disabled={checkoutPayMethod === 'finance'}
                          onClick={() => { if (checkoutPayMethod !== 'finance') { setCheckoutAmountType('deposit'); setShowStripeForm(false); setStripeClientSecret(null); } }}
                          className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all ${
                            checkoutAmountType === 'deposit' && checkoutPayMethod !== 'finance'
                              ? 'border-amber-500 bg-amber-500/10'
                              : checkoutPayMethod === 'finance'
                              ? 'border-zinc-800 bg-zinc-900/30 opacity-40 cursor-not-allowed'
                              : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${checkoutAmountType === 'deposit' && checkoutPayMethod !== 'finance' ? 'border-amber-500' : 'border-zinc-600'}`}>
                              {checkoutAmountType === 'deposit' && checkoutPayMethod !== 'finance' && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
                            </div>
                            <div>
                              <p className="text-white font-bold text-sm">Reserve with deposit</p>
                              <p className="text-zinc-500 text-xs mt-0.5">
                                Pay ${getDepositAmount(formData.service)} today · ${totalPrice ? totalPrice - getDepositAmount(formData.service) : '—'} at pickup
                              </p>
                            </div>
                          </div>
                          <span className={`text-2xl font-black ml-3 flex-shrink-0 ${checkoutAmountType === 'deposit' && checkoutPayMethod !== 'finance' ? 'text-amber-400' : 'text-white'}`}>
                            ${getDepositAmount(formData.service)}
                          </span>
                        </button>

                        {/* Full amount option */}
                        {totalPrice !== null && (
                          <button
                            type="button"
                            data-testid="checkout-amount-full"
                            onClick={() => { setCheckoutAmountType('full'); setShowStripeForm(false); setStripeClientSecret(null); }}
                            className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all ${
                              checkoutAmountType === 'full' || checkoutPayMethod === 'finance'
                                ? 'border-amber-500 bg-amber-500/10'
                                : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${checkoutAmountType === 'full' || checkoutPayMethod === 'finance' ? 'border-amber-500' : 'border-zinc-600'}`}>
                                {(checkoutAmountType === 'full' || checkoutPayMethod === 'finance') && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
                              </div>
                              <div>
                                <p className="text-white font-bold text-sm">Pay Full Amount</p>
                                <p className="text-zinc-500 text-xs mt-0.5">
                                  {checkoutPayMethod === 'finance' ? 'Required for financing' : 'Finance Available'}
                                </p>
                              </div>
                            </div>
                            <span className={`text-2xl font-black ml-3 flex-shrink-0 ${checkoutAmountType === 'full' || checkoutPayMethod === 'finance' ? 'text-amber-400' : 'text-white'}`}>
                              ${totalPrice}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ── Card 2: Payment Method ── */}
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-black text-sm font-black">2</span>
                        </div>
                        <span className="text-white font-bold">Payment method</span>
                      </div>
                      <div className="px-5 pb-5 space-y-2">
                        {/* Apple Pay row */}
                        <button
                          type="button"
                          data-testid="checkout-method-applepay"
                          onClick={() => { setPayMethodRow('apple_pay'); setCheckoutPayMethod('card'); setShowStripeForm(false); setStripeClientSecret(null); }}
                          className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${payMethodRow === 'apple_pay' ? 'border-amber-500/40 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 fill-white" viewBox="0 0 814 1000">
                                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105.5-42-175.8-127.1c-66.3-80.4-130.3-199.3-130.3-312.3 0-201.6 131.3-308.1 260.6-308.1 75 0 138.5 42.8 185.4 42.8 44.9 0 116.6-44.9 195.7-44.9zm-94.3-108.8c35.2 0 89.5 26.7 121.3 66.4-3.2 2.6-61.6 35.2-61.6 104.7 0 82.1 73.9 113.9 76 114.5-.6 1.9-11.5 40.2-38.8 80.4-24.4 35.8-50.7 71.6-89.5 71.6-38.2 0-48-24.4-90.8-24.4-41.5 0-52.7 23.2-88.4 23.2s-65.7-34.6-88.4-71.9c-28.5-44.2-52-113.9-52-180.2 0-120.4 78-184.3 154-184.3 38.8 0 71.9 26.7 96.3 26.7 24.4 0 62.6-28.5 101.4-26.7z"/>
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="text-white font-bold text-sm">Pay</p>
                              <p className="text-zinc-500 text-xs">Fast, secure checkout with Face ID</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                        </button>

                        {/* Card Payment row */}
                        <button
                          type="button"
                          data-testid="checkout-method-card"
                          onClick={() => { setPayMethodRow('card'); setCheckoutPayMethod('card'); setShowStripeForm(false); setStripeClientSecret(null); }}
                          className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${payMethodRow === 'card' ? 'border-amber-500/40 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
                              <CreditCard className="w-5 h-5 text-amber-400" />
                            </div>
                            <div className="text-left">
                              <p className="text-white font-bold text-sm">Card Payment</p>
                              <p className="text-zinc-500 text-xs">Visa · Mastercard · Amex · Discover</p>
                            </div>
                          </div>
                          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                        </button>

                        {/* Finance Options row */}
                        <button
                          type="button"
                          data-testid="checkout-method-finance"
                          disabled={totalPrice === null}
                          onClick={() => {
                            if (totalPrice !== null) {
                              const isOpen = payMethodRow === 'finance';
                              setPayMethodRow(isOpen ? 'card' : 'finance');
                              setCheckoutPayMethod(isOpen ? 'card' : 'finance');
                              if (!isOpen) setCheckoutAmountType('full');
                              setShowStripeForm(false);
                              setStripeClientSecret(null);
                            }
                          }}
                          className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                            payMethodRow === 'finance' ? 'border-amber-500/40 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                          } ${totalPrice === null ? 'opacity-40 cursor-not-allowed' : ''}`}
                          title={totalPrice === null ? 'Financing requires a known price. Please contact us for a quote.' : ''}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Banknote className="w-5 h-5 text-green-400" />
                            </div>
                            <div className="text-left">
                              <p className="text-white font-bold text-sm">Finance Options</p>
                              <p className="text-zinc-500 text-xs">Split the total into smaller payments</p>
                            </div>
                          </div>
                          {payMethodRow === 'finance'
                            ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                          }
                        </button>

                        {checkoutPayMethod === 'finance' && (
                          <div className="rounded-xl border border-amber-500/20 bg-zinc-900/60 p-3 space-y-2">
                            {[
                              { id: 'affirm',   icon: 'A', bg: '#4C7BF4', color: '#fff', name: 'Affirm',   badge: '3, 6, or 12 months', desc: 'Monthly payments · Rates from 0–36% APR' },
                              { id: 'klarna',   icon: 'K', bg: '#FF87B2', color: '#fff', name: 'Klarna',   badge: 'Pay in 4',            desc: '4 interest-free payments every 2 weeks' },
                              { id: 'afterpay', icon: 'A', bg: '#B2F5E4', color: '#000', name: 'Afterpay', badge: 'Pay in 4',            desc: '4 interest-free payments every 2 weeks' },
                            ].map(p => (
                              <button
                                key={p.id}
                                type="button"
                                data-testid={`bnpl-provider-${p.id}`}
                                onClick={() => setBnplProvider(p.id)}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                  bnplProvider === p.id ? 'border-amber-500/60 bg-zinc-800' : 'border-zinc-700/60 bg-zinc-900 hover:border-zinc-600'
                                }`}
                              >
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-lg"
                                  style={{ backgroundColor: p.bg, color: p.color }}>
                                  {p.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-white font-bold text-sm">{p.name}</span>
                                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">{p.badge}</span>
                                  </div>
                                  <p className="text-zinc-500 text-xs mt-0.5">{p.desc}</p>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${bnplProvider === p.id ? 'border-amber-500' : 'border-zinc-600'}`}>
                                  {bnplProvider === p.id && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
                                </div>
                              </button>
                            ))}
                            <p className="text-zinc-500 text-xs leading-relaxed px-1 pt-1">
                              Choose a provider, then tap Pay. You'll finish your application in a Stripe-secured sheet. Your booking is only confirmed once payment is approved.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Card 3: Review & Pay ── */}
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center flex-shrink-0">
                          <span className="text-zinc-400 text-sm font-black">3</span>
                        </div>
                        <span className="text-white font-bold">Review &amp; pay</span>
                      </div>
                      <div className="px-5 pb-5 space-y-4">
                        {/* Summary rows */}
                        <div className="space-y-2 pb-3 border-b border-dashed border-zinc-700/60">
                          <p className="text-white text-sm font-medium">
                            {formData.service}{formData.package ? ` — ${formData.package}` : ''}
                          </p>
                          {formData.tintShade && (
                            <div className="flex justify-between text-sm">
                              <span className="text-zinc-500">Tint shade</span>
                              <span className="text-zinc-400">{formData.tintShade}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-500">Date & Time</span>
                            <span className="text-zinc-300">{formData.date} · {formData.time}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-500">Vehicle</span>
                            <span className="text-zinc-300">{[formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ')}</span>
                          </div>
                          {totalPrice !== null && (
                            <div className="flex justify-between text-sm pt-1">
                              <span className="text-zinc-500">Total</span>
                              <span className="text-white font-bold">${totalPrice}</span>
                            </div>
                          )}
                        </div>

                        {/* Amount due today */}
                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-white font-bold text-base">You pay today</p>
                            {checkoutAmountType === 'deposit' && checkoutPayMethod !== 'finance' && totalPrice !== null && (
                              <p className="text-zinc-500 text-xs mt-0.5">
                                ${totalPrice - getDepositAmount(formData.service)} due at pickup
                              </p>
                            )}
                          </div>
                          <span className="text-amber-400 font-black text-3xl leading-none">${payAmount}</span>
                        </div>

                        {/* Pay button or Stripe form */}
                        {!showStripeForm ? (
                          <button
                            type="button"
                            data-testid="checkout-pay-btn"
                            onClick={handlePayNow}
                            disabled={bnplLoading || (checkoutPayMethod === 'finance' && !bnplProvider)}
                            className="w-full h-14 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60 text-black font-black text-base flex items-center justify-center gap-2 transition-colors"
                          >
                            {bnplLoading ? (
                              <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Checkout...</>
                            ) : checkoutPayMethod === 'finance' ? (
                              <><ExternalLink className="w-4 h-4" /> {bnplProvider ? `Continue with ${bnplProvider.charAt(0).toUpperCase() + bnplProvider.slice(1)} — $${payAmount}` : 'Select a provider above'}</>
                            ) : (
                              <><Lock className="w-4 h-4" /> Pay ${payAmount}</>
                            )}
                          </button>
                        ) : (
                          /* Inline Stripe form */
                          <div className="space-y-3">
                            {stripeIntentLoading && (
                              <div className="flex items-center justify-center py-8 gap-3">
                                <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                                <span className="text-zinc-400 text-sm">Setting up payment...</span>
                              </div>
                            )}
                            {stripeIntentError && (
                              <div className="text-red-400 text-sm text-center py-3 px-4 bg-red-500/5 rounded-xl border border-red-500/20">{stripeIntentError}</div>
                            )}
                            {stripeClientSecret && !stripeIntentLoading && (
                              <StripePaymentSheet
                                clientSecret={stripeClientSecret}
                                amount={payAmount}
                                onSuccess={handleStripePaymentSuccess}
                                onCancel={() => { setShowStripeForm(false); setStripeClientSecret(null); setStripeIntentError(null); }}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => { setShowStripeForm(false); setStripeClientSecret(null); setStripeIntentError(null); }}
                              className="w-full text-zinc-500 text-xs hover:text-zinc-400 text-center py-2 transition-colors"
                            >
                              <ChevronRight className="w-3 h-3 rotate-180 inline mr-1" />Change payment method
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                    );
                  })()
                  ) : (
                  /* ── No-deposit explicit confirm ── */
                  <div className="space-y-5">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-white mb-1">Confirm Your Appointment</h3>
                      <p className="text-zinc-400 text-sm">No deposit is required. Review your details and click below to confirm.</p>
                    </div>

                    {/* Booking summary */}
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-zinc-500">Name</span><p className="text-white font-medium">{formData.name}</p></div>
                        <div><span className="text-zinc-500">Service</span><p className="text-white font-medium">{formData.service}</p></div>
                        {formData.package && <div><span className="text-zinc-500">Package</span><p className="text-white font-medium">{formData.package}</p></div>}
                        <div><span className="text-zinc-500">Date</span><p className="text-white font-medium">{formData.date}</p></div>
                        <div><span className="text-zinc-500">Time</span><p className="text-white font-medium">{formData.time}</p></div>
                        <div><span className="text-zinc-500">Vehicle</span><p className="text-white font-medium">{formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}</p></div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      data-testid="confirm-free-booking-btn"
                      disabled={isSubmitting}
                      onClick={handleConfirmFreeBooking}
                      className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide text-base"
                    >
                      {isSubmitting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Confirming...</> : 'Confirm Appointment'}
                    </Button>

                    <Button
                      type="button"
                      data-testid="back-to-details-no-deposit-btn"
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="w-full border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                    >
                      Back
                    </Button>
                  </div>
                  )
                )}
              </div>
            )}
            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="py-4" data-testid="booking-confirmation">
                {/* Ticket Card */}
                <div
                  style={{
                    background: 'linear-gradient(160deg, #0a0a0c 0%, #111113 100%)',
                    border: '1px solid rgba(234,179,8,0.25)',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 0 60px rgba(234,179,8,0.08), 0 20px 40px rgba(0,0,0,0.6)',
                  }}
                >
                  {/* Header strip */}
                  <div
                    style={{
                      background: 'linear-gradient(90deg, #78350f 0%, #eab308 50%, #78350f 100%)',
                      padding: '14px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ color: '#000', fontWeight: 900, fontSize: '13px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                      PDX Window Tint
                    </span>
                    <span
                      style={{
                        background: 'rgba(0,0,0,0.25)',
                        color: '#000',
                        fontWeight: 800,
                        fontSize: '11px',
                        letterSpacing: '0.1em',
                        padding: '3px 10px',
                        borderRadius: '20px',
                      }}
                    >
                      CONFIRMED
                    </span>
                  </div>

                  {/* Main body */}
                  <div style={{ padding: '28px 24px 0' }}>
                    {/* Checkmark + headline */}
                    <div className="text-center mb-6">
                      <div
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          background: 'rgba(34,197,94,0.12)',
                          border: '2px solid rgba(34,197,94,0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <CheckCircle2 className="w-8 h-8" style={{ color: '#22c55e' }} />
                      </div>
                      <h3
                        style={{
                          color: '#eab308',
                          fontWeight: 900,
                          fontSize: 'clamp(1.6rem, 6vw, 2.2rem)',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          lineHeight: 1.1,
                          marginBottom: '6px',
                        }}
                      >
                        You're All Set!
                      </h3>
                      <p style={{ color: '#71717a', fontSize: '13px' }}>
                        A confirmation email is on its way to{' '}
                        <span style={{ color: '#a1a1aa' }}>{formData.email}</span>
                      </p>
                    </div>

                    {/* Perforated divider */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        margin: '0 -24px 24px',
                      }}
                    >
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#000', flexShrink: 0 }} />
                      <div style={{ flex: 1, borderTop: '2px dashed rgba(255,255,255,0.08)' }} />
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#000', flexShrink: 0 }} />
                    </div>

                    {/* Detail rows */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                      {[
                        { label: 'Name', value: formData.name },
                        { label: 'Service', value: formData.service, gold: true },
                        formData.package && { label: 'Package', value: formData.package },
                        formData.tintShade && { label: 'Tint Shade', value: formData.tintShade },
                        {
                          label: 'Date',
                          value: formData.date
                            ? new Date(formData.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                            : formData.date,
                        },
                        { label: 'Time', value: formData.time },
                        formData.vehicleYear && { label: 'Vehicle', value: `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}` },
                        stripePaymentSuccess && { label: 'Deposit Paid', value: `$${getDepositAmount(formData.service)} via Stripe`, green: true },
                      ].filter(Boolean).map((row) => (
                        <div key={row.label} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                          <span style={{ color: '#52525b', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', paddingTop: '1px' }}>
                            {row.label}
                          </span>
                          <span style={{
                            color: row.gold ? '#eab308' : row.green ? '#22c55e' : '#ffffff',
                            fontSize: '14px',
                            fontWeight: 700,
                            textAlign: 'right',
                          }}>
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Second perforated divider */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        margin: '0 -24px 20px',
                      }}
                    >
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#000', flexShrink: 0 }} />
                      <div style={{ flex: 1, borderTop: '2px dashed rgba(255,255,255,0.08)' }} />
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#000', flexShrink: 0 }} />
                    </div>

                    {/* Drop-off info */}
                    <div
                      style={{
                        background: 'rgba(234,179,8,0.06)',
                        border: '1px solid rgba(234,179,8,0.15)',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        marginBottom: '24px',
                        textAlign: 'center',
                      }}
                    >
                      <p style={{ color: '#eab308', fontWeight: 800, fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
                        Drop-Off Only
                      </p>
                      <p style={{ color: '#a1a1aa', fontSize: '13px' }}>17960 SW Alvord Lane, Beaverton, OR</p>
                    </div>
                  </div>

                  {/* Footer strip */}
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      padding: '16px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: '#3f3f46', fontSize: '11px' }}>pdxwindowtint.com</span>
                    <span style={{ color: '#3f3f46', fontSize: '11px' }}>·</span>
                    <span style={{ color: '#3f3f46', fontSize: '11px' }}>503-328-4130</span>
                  </div>
                </div>

                {/* Screenshot hint */}
                <p
                  style={{ color: '#52525b', fontSize: '12px', textAlign: 'center', marginTop: '12px', marginBottom: '20px' }}
                  data-testid="screenshot-hint"
                >
                  Screenshot this page to save your appointment details
                </p>

                {/* Action buttons */}
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => downloadICS(formData)}
                    data-testid="add-to-phone-calendar-btn"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '52px',
                      borderRadius: '999px',
                      border: '1px solid rgba(234,179,8,0.3)',
                      background: 'rgba(234,179,8,0.06)',
                      color: '#eab308',
                      fontWeight: 700,
                      fontSize: '14px',
                      letterSpacing: '0.04em',
                      cursor: 'pointer',
                    }}
                  >
                    <CalendarPlus className="w-4 h-4" />
                    Add to Calendar
                  </button>

                  <button
                    onClick={resetForm}
                    data-testid="book-another-btn"
                    style={{
                      width: '100%',
                      height: '52px',
                      borderRadius: '999px',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: '#71717a',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    Book Another Service
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
